/**
 * Inventory Lists Settings Tab
 *
 * CRUD management of predefined inventory lists.
 * - Create: simple Modal (name + description only)
 * - Edit: Drawer/SlideOver (name, description, product management)
 * Available only on parent (group) brands.
 * @see US-39.3
 */

import { useState, useCallback, useMemo } from 'react';
import { Plus, Trash2, Edit2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { Button, Badge, Modal, Drawer, Input, Textarea, SearchAutocomplete } from '@/components/ui';
import type { SearchAutocompleteOption } from '@/components/ui';
import { apiClient } from '@/api/client';
import { useQuery } from '@tanstack/react-query';
import { useInventoryLists, useInventoryListMutations } from '../hooks/useInventoryLists';
import { useDebounce } from '@/hooks';
import type { ApiSuccess, PaginatedResult, RawProductWithRelations } from '@bstock/shared';

async function searchRawProducts(search: string) {
  const response = await apiClient.get<ApiSuccess<PaginatedResult<RawProductWithRelations>>>('/raw-products', {
    params: { search, limit: 20 },
  });
  return response.data.data;
}

interface SelectedProduct {
  id: string;
  name: string;
  sku: string;
}

export function InventoryListsSettingsTab() {
  const { data: lists, isLoading } = useInventoryLists();
  const { create, update, replaceItems, remove } = useInventoryListMutations();

  // ── Create Modal state ──
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');

  // ── Edit Drawer state ──
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [editLoading, setEditLoading] = useState(false);

  // ── Product search (edit drawer) ──
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data: searchResults } = useQuery({
    queryKey: ['raw-products-search-lists', debouncedSearch],
    queryFn: () => searchRawProducts(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  });

  const selectedProductIds = useMemo(() => new Set(selectedProducts.map((p) => p.id)), [selectedProducts]);

  const productOptions = useMemo<SearchAutocompleteOption[]>(() => {
    return (searchResults?.data ?? [])
      .filter((p) => !selectedProductIds.has(p.id))
      .map((p) => ({
        id: p.id,
        label: p.name,
        sublabel: `SKU: ${p.sku}${p.category ? ` | ${p.category.name}` : ''}`,
      }));
  }, [searchResults?.data, selectedProductIds]);

  // ── Create handlers ──

  const handleOpenCreate = useCallback(() => {
    setCreateName('');
    setCreateDescription('');
    setIsCreateOpen(true);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!createName.trim()) return;
    await create.mutateAsync({
      name: createName.trim(),
      description: createDescription.trim() || null,
    });
    setIsCreateOpen(false);
  }, [createName, createDescription, create]);

  // ── Edit handlers ──

  const handleOpenEdit = useCallback(
    async (listId: string) => {
      const list = lists?.find((l) => l.id === listId);
      if (!list) return;

      setEditingListId(listId);
      setEditName(list.name);
      setEditDescription(list.description ?? '');
      setSearchQuery('');
      setEditLoading(true);

      try {
        const response = await apiClient.get<ApiSuccess<any>>(`/inventory-lists/${listId}`);
        const fullList = response.data.data;
        setSelectedProducts(
          fullList.items.map((item: any) => ({
            id: item.rawProductId,
            name: item.rawProductName,
            sku: item.rawProductSku ?? '',
          })),
        );
      } catch {
        setSelectedProducts([]);
      } finally {
        setEditLoading(false);
      }
    },
    [lists],
  );

  const handleCloseEdit = useCallback(() => {
    setEditingListId(null);
    setSelectedProducts([]);
    setSearchQuery('');
  }, []);

  const handleProductSelect = useCallback(
    (option: SearchAutocompleteOption | null) => {
      if (option) {
        const product = searchResults?.data?.find((p) => p.id === option.id);
        if (product) {
          setSelectedProducts((prev) => [...prev, { id: product.id, name: product.name, sku: product.sku }]);
        }
      }
    },
    [searchResults?.data],
  );

  const handleRemoveProduct = useCallback((productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingListId || !editName.trim()) return;

    await update.mutateAsync({
      id: editingListId,
      data: { name: editName.trim(), description: editDescription.trim() || null },
    });

    if (selectedProducts.length > 0) {
      await replaceItems.mutateAsync({
        id: editingListId,
        data: { rawProductIds: selectedProducts.map((p) => p.id) },
      });
    }

    handleCloseEdit();
  }, [editingListId, editName, editDescription, selectedProducts, update, replaceItems, handleCloseEdit]);

  // ── Toggle / Delete ──

  const handleToggleActive = useCallback(
    async (listId: string, currentActive: boolean) => {
      await update.mutateAsync({ id: listId, data: { isActive: !currentActive } });
    },
    [update],
  );

  const handleDelete = useCallback(
    async (listId: string, listName: string) => {
      if (window.confirm(`Supprimer la liste "${listName}" ?`)) {
        await remove.mutateAsync(listId);
      }
    },
    [remove],
  );

  if (isLoading) {
    return <div className="text-center py-8 text-neutral-500">Chargement...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-dark-500">Listes d'inventaire</h2>
          <p className="text-sm text-neutral-500">
            Definissez des listes de produits recurrentes pour les inventaires partiels
          </p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle liste
        </Button>
      </div>

      {/* Lists table */}
      {!lists || lists.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 bg-white rounded-lg border border-neutral-200">
          Aucune liste definie. Creez votre premiere liste pour simplifier les inventaires quotidiens.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200 divide-y divide-neutral-100">
          {lists.map((list) => (
            <div key={list.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-dark-500">{list.name}</span>
                  <Badge variant={list.isActive ? 'success' : 'neutral'}>
                    {list.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {list.description && (
                  <p className="text-sm text-neutral-500 truncate">{list.description}</p>
                )}
                <p className="text-xs text-neutral-400">{list.itemCount} produits</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleActive(list.id, list.isActive)}
                  className="p-1.5 rounded hover:bg-neutral-100 transition-colors"
                  title={list.isActive ? 'Desactiver' : 'Activer'}
                >
                  {list.isActive ? (
                    <ToggleRight className="w-5 h-5 text-success-600" />
                  ) : (
                    <ToggleLeft className="w-5 h-5 text-neutral-400" />
                  )}
                </button>
                <button
                  onClick={() => handleOpenEdit(list.id)}
                  className="p-1.5 rounded hover:bg-neutral-100 transition-colors"
                  title="Modifier"
                >
                  <Edit2 className="w-4 h-4 text-neutral-500" />
                </button>
                <button
                  onClick={() => handleDelete(list.id, list.name)}
                  className="p-1.5 rounded hover:bg-danger-50 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4 text-danger-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create Modal (name + description only) ── */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nouvelle liste"
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Nom *"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="Ex: Frais quotidien"
            maxLength={100}
          />
          <Textarea
            label="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            placeholder="Description optionnelle"
            rows={2}
            maxLength={500}
          />
          <div className="flex justify-end gap-3 pt-2 border-t border-neutral-200">
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!createName.trim() || create.isPending}
              isLoading={create.isPending}
            >
              Creer
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Edit Drawer (full management) ── */}
      <Drawer
        open={!!editingListId}
        onOpenChange={(open) => { if (!open) handleCloseEdit(); }}
        title="Modifier la liste"
        size="large"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleCloseEdit}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={!editName.trim() || update.isPending || replaceItems.isPending}
              isLoading={update.isPending || replaceItems.isPending}
            >
              Enregistrer
            </Button>
          </div>
        }
      >
        {editLoading ? (
          <div className="text-center py-8 text-neutral-500">Chargement...</div>
        ) : (
          <div className="space-y-6">
            {/* Name */}
            <Input
              label="Nom *"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Ex: Frais quotidien"
              maxLength={100}
            />

            {/* Description */}
            <Textarea
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Description optionnelle"
              rows={2}
              maxLength={500}
            />

            {/* Product search */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Produits ({selectedProducts.length})
              </label>
              <SearchAutocomplete
                value={null}
                options={productOptions}
                onChange={handleProductSelect}
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                placeholder="Rechercher un produit..."
                isLoading={debouncedSearch.length >= 2 && !searchResults}
                keepFocusOnSelect
              />
            </div>

            {/* Selected products list */}
            {selectedProducts.length > 0 ? (
              <div className="border border-neutral-200 rounded-lg max-h-80 overflow-auto">
                {selectedProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-neutral-400 w-6 text-right">{index + 1}.</span>
                      <span className="text-sm text-dark-500 truncate">{product.name}</span>
                      <span className="text-xs text-neutral-400">{product.sku}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveProduct(product.id)}
                      className="p-1 rounded hover:bg-danger-50"
                    >
                      <X className="w-3.5 h-3.5 text-danger-500" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                Aucun produit. Utilisez la recherche ci-dessus pour ajouter des produits.
              </p>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
